import type { PropertyName } from "@frui.ts/helpers/dist";
import DetailViewModel from "../../../../detailViewModel";
import type ChargeResponse from "entities/chargeResponse";
import type { EntityNames } from "config";
import { Modules } from "config";
import type { interfaces } from "inversify";
import LocalizationService from "services/localizationService";
import ChargesRepository from "repositories/chargesRepository";
import EnumService from "services/enumService";
import type GetSingleChargeDetailResponse from "entities/getSingleChargeDetailResponse";
import { action, computed } from "mobx";
import type SingleChargeDetailResponse from "entities/singleChargeDetailResponse";
import type SingleChargeOrderDetailResponse from "entities/singleChargeOrderDetailResponse";
import type DistributorOrderDetailResponse from "entities/distributorOrderDetailResponse";
import type KioskOrderDetailResponse from "entities/kioskOrderDetailResponse";
import type ChargeOrderStatus from "entities/chargeOrderStatus";
import type ChargeDisplayStatus from "entities/chargeDisplayStatus";
import type PaymentType from "entities/paymentType";
import type SourceType from "entities/sourceType";
import type SingleChargeNotificationPreferencesResponse from "entities/singleChargeNotificationPreferencesResponse";
import type PayGatePayment from "entities/payGatePaymentResponse";
import type ChargeOrderPaymentDisplayType from "entities/chargeOrderPaymentDisplayType";
import PriceListService from "services/priceListService";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import type EshopTransferOrderDetailResponse from "entities/eshopTransferOrderDetailResponse";
import type EshopPayGateOrderDetailResponse from "entities/eshopPayGateOrderDetailResponse";
import type SingleChargeVehicleRegisterVehicleDetailResponse from "entities/singleChargeVehicleRegisterVehicleDetailResponse";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import { emptyValue } from "utils/emptyValue";

export default class ChargeDetailInfoViewModel extends DetailViewModel<GetSingleChargeDetailResponse> {
  protected readonly modelName: EntityNames = "charge";

  constructor(
    protected itemId: string,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected chargeRepository: ChargesRepository
  ) {
    super(itemId);
    this.navigationName = "";
  }

  protected loadDetail(): Promise<GetSingleChargeDetailResponse> | GetSingleChargeDetailResponse {
    return this.chargeRepository.getOne(this.itemId);
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get chargeDetail() {
    return this.item.chargeDetail;
  }

  @action.bound
  priceListItem() {
    return this.priceListService.priceListItemById(this.chargeDetail.priceListItemId);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, `chargeDetail.${code}`, size);
  }

  translateSubAttribute(model: "distributorOrderDetailResponse", attribute: PropertyName<DistributorOrderDetailResponse>): string;
  translateSubAttribute(
    model: "eshopTransferOrderDetailResponse",
    attribute: PropertyName<EshopTransferOrderDetailResponse>
  ): string;
  translateSubAttribute(
    model: "eshopPayGateOrderDetailResponse",
    attribute: PropertyName<EshopPayGateOrderDetailResponse>
  ): string;
  translateSubAttribute(model: "payGatePayment", attribute: PropertyName<PayGatePayment>): string;
  translateSubAttribute(model: "kioskOrderDetailResponse", attribute: PropertyName<KioskOrderDetailResponse>): string;
  translateSubAttribute(model: "singleChargeDetailResponse", attribute: PropertyName<SingleChargeDetailResponse>): string;
  translateSubAttribute(
    model: "vehicleRegisterVehicleDetailResponse",
    attribute: PropertyName<SingleChargeVehicleRegisterVehicleDetailResponse>
  ): string;

  translateSubAttribute(
    model: "singleChargeOrderDetailResponse",
    attribute: PropertyName<SingleChargeOrderDetailResponse>
  ): string;

  translateSubAttribute(
    model: "singleChargeContactDetailResponse",
    attribute: PropertyName<SingleChargeNotificationPreferencesResponse>
  ): string;

  translateSubAttribute(model: string, attribute: string) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateChargeOrderStatus(chargeOrderStatus: ChargeOrderStatus) {
    return this.localizationService.translateEnum("chargeOrderStatus", chargeOrderStatus);
  }

  translateChargeDisplayStatus(chargeDisplayStatus: ChargeDisplayStatus) {
    return this.localizationService.translateEnum("chargeDisplayStatus", chargeDisplayStatus);
  }

  translateSourceType(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  translatePaymentType(paymentType: PaymentType) {
    return this.localizationService.translateEnum("paymentType", paymentType);
  }
  translatePaymentDisplayType(paymentType: ChargeOrderPaymentDisplayType) {
    return this.localizationService.translateEnum("paymentDisplayType", paymentType);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: VehicleRegisterVehicleFuelType[]) {
    const translation = fuelType.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleRegisterSynchronizationStatus(status: VehicleRegisterSynchronizationStatus) {
    return this.localizationService.translateEnum("vehicleRegisterSynchronizationStatus", status);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateCountryId(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? emptyValue;
  }

  formatHeight(amount: number) {
    return `${amount} kg`;
  }

  translateFuelType() {
    const priceItem = this.priceListItem();
    if (!priceItem) {
      return;
    }

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === priceItem.fuelTypeId);
    if (!fuelType) {
      return;
    }
    this.chargeDetail._fuelTypeId = fuelType.id;

    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  translateChargeType() {
    const priceItem = this.priceListItem();
    if (!priceItem) {
      return;
    }

    const chargeType = this.enumService.values("charge_types").find(chargeType => chargeType.id === priceItem.chargeTypeId);
    if (!chargeType) {
      return;
    }
    this.chargeDetail._chargeTypeId = chargeType.id;
    return this.localizationService.translateEnum("chargeValidity", chargeType.validFor);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse) =>
      new ChargeDetailInfoViewModel(
        charge.chargeId,
        container.get(LocalizationService),
        container.get(PriceListService),
        container.get(EnumService),
        container.get(ChargesRepository)
      );
  }
}
